import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Lock, User as UserIcon, Eye, EyeOff, Camera, Building2, CheckCircle2, Calendar, AlertTriangle, RefreshCw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link as RLink } from "react-router-dom";
import { toast } from "sonner";


const ROLES = [
  { id: "talent", label: "Je suis un Talent", desc: "Mannequin, modèle, acteur, danseur, performer, influenceur — vous incarnez l'image.", icon: Camera },
  { id: "creator", label: "Je suis un Créateur", desc: "Photographe, styliste, MUA, vidéaste, marque, studio, agence — vous créez et recherchez des talents.", icon: Building2 },
];

export default function Register() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const presetRole = new URLSearchParams(window.location.search).get("role");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    password: "", confirm_password: "",
    role: presetRole === "creator" ? "creator" : "talent",
    birth_date: "",
    confirmed_adult: false, accepted_terms: false, accepted_privacy: false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => (v) => setForm({ ...form, [k]: !!v });

  // Compute age client-side just for friendly UX (real validation is server-side)
  const computedAge = (() => {
    if (!form.birth_date) return null;
    try {
      const bd = new Date(form.birth_date);
      const today = new Date();
      let a = today.getFullYear() - bd.getFullYear();
      const m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) a--;
      return a;
    } catch { return null; }
  })();
  const tooYoung = computedAge !== null && computedAge < 18;
  const allChecked = form.confirmed_adult && form.accepted_terms && form.accepted_privacy;
  const canSubmit = !submitting && !tooYoung && allChecked && !!form.birth_date;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm_password) {
      setErr("Les mots de passe ne correspondent pas");
      return;
    }
    if (form.password.length < 8) {
      setErr("Le mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (tooYoung) {
      setErr("Helencia est actuellement réservée aux utilisateurs majeurs (18 ans et plus).");
      return;
    }
    if (!allChecked) {
      setErr("Veuillez cocher toutes les cases obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, origin: window.location.origin });
      if (data.auto_login) {
        if (data.session_token) localStorage.setItem("helencia_token", data.session_token);
        setUser(data.user);
        await checkAuth();
        toast.success(`Bienvenue ${data.user?.first_name || ""} !`);
        navigate(data.user?.role === "talent" ? "/talent" : "/createurs", { replace: true });
        return;
      }
      setSuccess(data);
      toast.success("Compte créé !");
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI / Pydantic validation: show the first field error in French.
        const first = detail[0];
        const fieldMap = {
          email: "email", password: "mot de passe", first_name: "prénom",
          last_name: "nom", birth_date: "date de naissance", role: "type de compte",
        };
        const fieldKey = first?.loc?.slice(-1)?.[0];
        const fieldLabel = fieldMap[fieldKey] || fieldKey || "Champ";
        if (first?.type === "missing") msg = `Le champ "${fieldLabel}" est requis.`;
        else if (first?.type === "value_error" && first?.msg?.includes("email")) msg = "Adresse email invalide.";
        else msg = `${fieldLabel} : ${first?.msg || "valeur invalide"}.`;
      } else {
        msg = e.userMessage || "Impossible de créer le compte. Réessayez dans un instant.";
      }
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthShell eyebrow="Compte créé" title="Confirmez votre adresse email." subtitle={`Un email de confirmation a été envoyé à ${success.email}. Cliquez sur le lien pour activer votre compte.`}>
        <RegisterSuccess success={success} firstName={form.first_name} navigate={navigate} />
      </AuthShell>
    );
  }

  return (
    <AuthShell side="left" eyebrow="Créer un compte" title="Rejoignez la maison Helencia." subtitle="Créez votre compte gratuit et commencez à recevoir des opportunités.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="caption-label">Prénom</Label>
            <div className="relative mt-2">
              <UserIcon className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <Input value={form.first_name} onChange={update("first_name")} required placeholder="Sophie" className="rounded-xl pl-10 h-11" data-testid="register-firstname" />
            </div>
          </div>
          <div>
            <Label className="caption-label">Nom</Label>
            <Input value={form.last_name} onChange={update("last_name")} required placeholder="Moreau" className="rounded-xl mt-2 h-11" data-testid="register-lastname" />
          </div>
        </div>

        <div>
          <Label className="caption-label">Email</Label>
          <div className="relative mt-2">
            <Mail className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input type="email" value={form.email} onChange={update("email")} required placeholder="vous@email.com" className="rounded-xl pl-10 h-11" data-testid="register-email" />
          </div>
        </div>

        <div>
          <Label className="caption-label">Mot de passe</Label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input type={showPwd ? "text" : "password"} value={form.password} onChange={update("password")} required minLength={8} placeholder="8 caractères minimum" className="rounded-xl pl-10 pr-10 h-11" data-testid="register-password" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#A26F3E]" tabIndex={-1}>
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="caption-label">Confirmer le mot de passe</Label>
          <Input type={showPwd ? "text" : "password"} value={form.confirm_password} onChange={update("confirm_password")} required placeholder="Répétez votre mot de passe" className="rounded-xl mt-2 h-11" data-testid="register-confirm" />
        </div>

        <div>
          <Label className="caption-label">Date de naissance</Label>
          <div className="relative mt-2">
            <Calendar className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="date"
              value={form.birth_date}
              onChange={update("birth_date")}
              required
              max={new Date(Date.now() - 18*365.25*24*3600*1000).toISOString().slice(0,10)}
              className="rounded-xl pl-10 h-11"
              data-testid="register-birthdate"
            />
          </div>
          {tooYoung && (
            <p className="mt-2 text-xs text-[#A3483F]" data-testid="register-too-young">
              Helencia est actuellement réservée aux utilisateurs majeurs (18 ans et plus).
            </p>
          )}
        </div>

        <div>
          <Label className="caption-label">Type de compte</Label>
          <div className="grid grid-cols-1 gap-2 mt-3">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = form.role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setForm({ ...form, role: r.id })}
                  className={`text-left p-4 rounded-2xl border-2 transition flex items-start gap-3 ${active ? "border-[#C68E59] bg-[#FAF3EA]" : "border-[#EADDC9] bg-white hover:border-[#D4A574]"}`}
                  data-testid={`register-role-${r.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${active ? "bg-[#C68E59] text-white" : "bg-[#FAF3EA] text-[#A26F3E]"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A]">{r.label}</p>
                    <p className="text-xs text-[#8C7D6B]">{r.desc}</p>
                  </div>
                  {active && <CheckCircle2 className="w-4 h-4 text-[#C68E59] ml-auto flex-shrink-0 mt-2" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* RGPD checkboxes */}
        <div className="space-y-3 rounded-2xl bg-[#FAF3EA] p-4 border border-[#EADDC9]">
          <p className="caption-label text-[#A26F3E]">Conformité &amp; majorité</p>
          <ConsentRow
            id="confirmed_adult"
            checked={form.confirmed_adult}
            onChange={toggle("confirmed_adult")}
            label="Je certifie avoir au moins 18 ans."
            testid="register-consent-adult"
          />
          <ConsentRow
            id="accepted_terms"
            checked={form.accepted_terms}
            onChange={toggle("accepted_terms")}
            testid="register-consent-terms"
            label={<>J'accepte les <RLink to="/cgu" target="_blank" className="underline text-[#1A1A1A] hover:text-[#C68E59]">Conditions Générales d'Utilisation</RLink>.</>}
          />
          <ConsentRow
            id="accepted_privacy"
            checked={form.accepted_privacy}
            onChange={toggle("accepted_privacy")}
            testid="register-consent-privacy"
            label={<>J'accepte la <RLink to="/confidentialite" target="_blank" className="underline text-[#1A1A1A] hover:text-[#C68E59]">Politique de confidentialité</RLink>.</>}
          />
        </div>

        {err && (
          <div className="rounded-xl bg-[#FEEFEC] border border-[#F2C0B7] text-[#A3483F] text-sm px-4 py-3" data-testid="register-error">
            {err}
          </div>
        )}

        <Button type="submit" disabled={!canSubmit} className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12 disabled:opacity-50" data-testid="register-submit">
          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création…</>) : "Créer mon compte"}
        </Button>
      </form>


      <p className="text-sm text-[#6E4B2A] text-center mt-6">
        Vous avez déjà un compte ?{" "}
        <Link to="/login" className="font-semibold text-[#1A1A1A] underline" data-testid="register-login-link">
          Se connecter
        </Link>
      </p>
    </AuthShell>
  );
}


function ConsentRow({ id, checked, onChange, label, testid }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer text-sm leading-snug text-[#3C2C1D]">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="mt-0.5 border-[#A26F3E] data-[state=checked]:bg-[#C68E59] data-[state=checked]:border-[#C68E59]" data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

/**
 * Success state after a successful registration.
 *
 * Shows a clear confirmation that the email was sent, a "Renvoyer l'email"
 * button with a 60-second cooldown (anti-abuse, matches the backend rate limit),
 * and surfaces real errors when the email provider failed instead of silently
 * pretending all is well.
 */
function RegisterSuccess({ success, firstName, navigate }) {
  const [cooldown, setCooldown] = useState(60); // server enforces 60s; mirror it
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // { ok, msg } | null

  // Tick the cooldown down once per second so the button re-enables on its own.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setResendStatus(null);
    try {
      const { data } = await api.post("/auth/resend-verification", {
        email: success.email,
        origin: window.location.origin,
      });
      const ok = data?.email_sent !== false;
      setResendStatus({
        ok,
        msg: ok
          ? "Email renvoyé. Vérifiez votre boîte (et les spams)."
          : (data?.email_error || "Échec d'envoi. Contactez le support si cela persiste."),
      });
      if (ok) {
        setCooldown(60);
        toast.success("Email renvoyé");
      } else {
        toast.error("Échec de l'envoi");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : e.userMessage || "Impossible de renvoyer l'email.";
      setResendStatus({ ok: false, msg });
      // Prefer structured header (set by backend on 429), fall back to regex.
      const headerRetry = parseInt(e.response?.headers?.["x-retry-after-seconds"] || e.response?.headers?.["retry-after"] || "", 10);
      if (Number.isFinite(headerRetry) && headerRetry > 0) {
        setCooldown(headerRetry);
      } else {
        const m = msg.match(/(\d+)\s*s/);
        if (m) setCooldown(Math.max(parseInt(m[1], 10), 1));
      }
      toast.error("Échec de l'envoi");
    } finally {
      setResending(false);
    }
  };

  const emailFailed = success.email_sent === false;

  return (
    <div className="rounded-3xl border border-[#E6C29A] bg-gradient-to-br from-[#FAF3EA] to-white p-6 text-center" data-testid="register-success">
      <div className="w-12 h-12 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-6 h-6 text-[#D4A574]" />
      </div>
      <p className="font-serif text-2xl text-[#1A1A1A]">Bienvenue {firstName} !</p>
      <p className="text-sm text-[#6E4B2A] mt-2">
        Un email de confirmation a été envoyé à <strong>{success.email}</strong>.
      </p>
      <p className="text-xs text-[#8C7D6B] mt-2 leading-relaxed">
        Le lien expire dans 48h. Pensez à vérifier vos <strong>spams / promotions</strong> si l'email tarde.
      </p>

      {/* Email-delivery warning surfaced only when the backend reports a real failure. */}
      {emailFailed && (
        <div className="mt-4 p-3 bg-[#FEEFEC] border border-[#F2C0B7] rounded-xl text-left flex gap-2 items-start" data-testid="register-email-failed">
          <AlertTriangle className="w-4 h-4 text-[#A3483F] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-[#A3483F]">L'email n'a pas pu être envoyé.</p>
            {success.email_error && (
              <p className="text-[11px] text-[#6E4B2A] mt-1 break-words">{success.email_error}</p>
            )}
            <p className="text-[11px] text-[#6E4B2A] mt-1">Réessayez ou contactez <a href="mailto:support@helencia.fr" className="underline">support@helencia.fr</a>.</p>
          </div>
        </div>
      )}

      {/* DEV: direct verification link — only present when backend exposes it (dev/preview). */}
      {success.dev_verification_link && (
        <div className="mt-5 p-3 bg-white rounded-xl border border-[#EADDC9] text-left">
          <p className="caption-label text-[#A26F3E] mb-1">Mode DEV — Lien direct</p>
          <a href={success.dev_verification_link} className="text-xs text-[#1A1A1A] break-all underline" data-testid="register-dev-link">
            {success.dev_verification_link}
          </a>
        </div>
      )}

      {/* Resend button with cooldown matching the backend per-user rate limit (60s). */}
      <Button
        type="button"
        variant="outline"
        onClick={handleResend}
        disabled={cooldown > 0 || resending}
        className="mt-5 w-full rounded-full border-[#EADDC9] hover:bg-[#FAF3EA] h-11 disabled:opacity-50"
        data-testid="register-resend-email"
      >
        {resending ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
        ) : cooldown > 0 ? (
          <>Renvoyer l'email ({cooldown}s)</>
        ) : (
          <><RefreshCw className="w-4 h-4 mr-2" /> Renvoyer l'email de confirmation</>
        )}
      </Button>

      {resendStatus && (
        <p className={`text-xs mt-2 ${resendStatus.ok ? "text-[#3D8168]" : "text-[#A3483F]"}`} data-testid="register-resend-status">
          {resendStatus.msg}
        </p>
      )}

      <Button onClick={() => navigate("/login")} className="mt-3 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="register-go-login">
        Aller à la connexion
      </Button>
    </div>
  );
}
